"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Textarea,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ImageIcon,
  XCircleIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useApproveKycRequestMutation,
  useKycRequestDetailQuery,
  useRejectKycRequestMutation,
} from "../api/kyc-queries";

const TIER_LABELS: Record<string, string> = {
  unverified: "ບໍ່ໄດ້ຢືນຢັນ",
  basic: "ພື້ນຖານ",
  verified: "ຢືນຢັນແລ້ວ",
  premium: "ພຣີມຽມ",
};

const STATUS_STYLE: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  submitted: { label: "ລໍຖ້າກວດສອບ", variant: "default" },
  under_review: { label: "ກຳລັງກວດສອບ", variant: "secondary" },
  approved: { label: "ອະນຸມັດແລ້ວ", variant: "outline" },
  rejected: { label: "ປະຕິເສດ", variant: "destructive" },
};

function DocumentCard({
  title,
  url,
}: {
  title: string;
  url: string | null;
}) {
  return (
    <Card className="rounded-xl border-border">
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {url ? (
          <div className="overflow-hidden rounded-lg border bg-muted/20">
            <AppImage
              src={url}
              alt={title}
              className="max-h-[420px] w-full"
              fit="contain"
            />
          </div>
        ) : (
          <div className="flex h-48 flex-col items-center justify-center rounded-lg border border-dashed text-muted-foreground">
            <ImageIcon className="mb-2 size-10" />
            <p className="text-sm">ບໍ່ມີເອກະສານ</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function KycRequestDetailPage() {
  const { id } = useParams({ from: "/app/wallet/kyc-requests/$id" });
  const nav = useNavigate();
  const { data: detail, isLoading } = useKycRequestDetailQuery(id);
  const approveMutation = useApproveKycRequestMutation(id);
  const rejectMutation = useRejectKycRequestMutation(id);
  const [rejectReason, setRejectReason] = useState("");

  const headerBreadcrumb = (
    <div className="flex items-center gap-3">
      <Breadcrumb
        items={[
          { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
          { label: "ຄຳຂໍ KYC", to: "/app/wallet/kyc-requests" },
          {
            label: detail
              ? `#${detail.id.slice(0, 8).toUpperCase()}`
              : "ລາຍລະອຽດ",
          },
        ]}
      />
    </div>
  );

  if (isLoading) {
    return (
      <>
        <Header>{headerBreadcrumb}</Header>
        <Main>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-4 h-[400px] rounded-xl" />
        </Main>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <Header>{headerBreadcrumb}</Header>
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
            ບໍ່ພົບຄຳຂໍ KYC
          </div>
        </Main>
      </>
    );
  }

  const status = STATUS_STYLE[detail.status] ?? {
    label: detail.status,
    variant: "outline" as const,
  };
  const isReviewable =
    detail.status === "submitted" || detail.status === "under_review";

  return (
    <>
      <Header>{headerBreadcrumb}</Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-2xl tracking-tight">ຄຳຂໍ KYC</h2>
              <Badge variant={status.variant}>{status.label}</Badge>
            </div>
            <p className="text-muted-foreground text-sm">ລະຫັດ: {detail.id}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/wallet/kyc-requests" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="mb-4 grid gap-4 lg:grid-cols-2">
          <DocumentCard title="ເອກະສານ ID" url={detail.idDocumentUrl} />
          <DocumentCard title="ຮູບ Selfie" url={detail.selfieUrl} />
        </div>

        <Card className="rounded-xl border-border">
          <CardContent className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  ລູກຄ້າ
                </p>
                <p className="mt-1 font-medium text-sm">
                  {detail.fullName ?? detail.customerName ?? "—"}
                </p>
                <p className="text-muted-foreground text-xs">
                  {detail.customerEmail ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  ເລກບັດປະຈຳຕົວ
                </p>
                <p className="mt-1 font-mono text-sm">
                  {detail.idNumber ?? "—"}
                </p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  ລະດັບປັດຈຸບັນ
                </p>
                <p className="mt-1 font-medium text-sm">
                  {TIER_LABELS[detail.currentKycTier] ?? detail.currentKycTier}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  ລະດັບທີ່ຂໍ
                </p>
                <p className="mt-1 font-semibold text-secondary text-sm">
                  {TIER_LABELS[detail.requestedTier] ?? detail.requestedTier}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  ວັນທີຍື່ນ
                </p>
                <p className="mt-1 text-sm">
                  {formatDateTimeLocal(detail.createdAt)}
                </p>
              </div>
            </div>

            {detail.rejectReason ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-destructive text-sm">
                {detail.rejectReason}
              </div>
            ) : null}

            {isReviewable ? (
              <RequirePermissions all={["compliance:update"]}>
                <div className="flex flex-wrap gap-3 border-t pt-4">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        disabled={approveMutation.isPending}
                        className="gap-2"
                      >
                        <CheckCircleIcon className="size-4" />
                        ອະນຸມັດ KYC
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>ອະນຸມັດ KYC?</AlertDialogTitle>
                        <AlertDialogDescription>
                          ຈະປ່ຽນ tier ເປັນ{" "}
                          {TIER_LABELS[detail.requestedTier] ??
                            detail.requestedTier}{" "}
                          ແລະ ປິດຄຳຂໍນີ້
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => approveMutation.mutate()}
                        >
                          ຢືນຢັນອະນຸມັດ
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        disabled={rejectMutation.isPending}
                        className="gap-2"
                      >
                        <XCircleIcon className="size-4" />
                        ປະຕິເສດ
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>ປະຕິເສດ KYC?</AlertDialogTitle>
                        <AlertDialogDescription>
                          ລະບຸເຫດຜົນ (ບໍ່ບັງຄັບ) ເພື່ອແຈ້ງລູກຄ້າ
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <Textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="ເຫດຜົນການປະຕິເສດ..."
                        rows={3}
                      />
                      <AlertDialogFooter>
                        <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() =>
                            rejectMutation.mutate(rejectReason || undefined)
                          }
                        >
                          ຢືນຢັນປະຕິເສດ
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </RequirePermissions>
            ) : null}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
